import { FormFieldController, FormFieldState } from "@itk/react-form";
import { Button, IconButton } from "@itk/mui-components";
import { hasValue } from "@itk/typescript-utils";
import { Box, ButtonBase, CircularProgress, InputAdornment, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { KeyboardEvent, MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { HexColorPicker } from "react-colorful";
import { SelectedColor } from "src/components/color-picker-types";
import { GenericDialog } from "src/components/generic-dialog";
import { ncsColorActions } from "src/components/ncs-colors/ncs-colors-store";
import { NcsColorGet } from "src/components/ncs-colors/ncs-colors-types";
import { RootState } from "src/store";
import { CONSTANTS } from "src/utils/constants";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { addRecentNcsColorCode, findNearestNcsColor, getRecentNcsColorCodes, hexToRgb, ncsCodeMatchesSearch, rgbToLab } from "./ncs-color-utils";
import { useNcsColorPickerT } from "src/translation-hooks/ncs-color-picker";

const mapStateToProps = (state: RootState) => ({ ncsColors: state.generalReducer.ncsColorsReducer.ncsColors });

const ownActions = { getNcsColorsRequest: ncsColorActions.getNcsColorsRequest };

type OwnProps = {
  formFieldState: FormFieldState<string>;
  formFieldController: FormFieldController<string>;
  disabled?: boolean;
  onChange?: (color: SelectedColor | null) => void;
  label?: string;
  // set when this component is mounted as the direct result of picking "NCS" from the color-system
  // chooser, so the dialog opens immediately instead of requiring a second click
  openOnMount?: boolean;
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & typeof ownActions;

const NcsColorPickerBase = (props: Props) => {
  const { formFieldState, formFieldController, disabled, onChange, label, openOnMount, ncsColors, getNcsColorsRequest } = props;
  const theme = useTheme();
  const ncsColorPickerT = useNcsColorPickerT();
  const genericTranslationsT = useGenericTranslationsT();

  const [isOpen, setIsOpen] = useState(() => !!openOnMount);
  const [searchTerm, setSearchTerm] = useState("");
  const [recentCodes, setRecentCodes] = useState<string[]>(() => getRecentNcsColorCodes());
  // never pushed to formFieldController/onChange until confirmed, so dragging the picker never
  // fires the parent's save/network call on every pixel of movement
  const [pickedHex, setPickedHex] = useState(CONSTANTS.DEFAULT_PICKER_HEX);

  // ncsColors is shared global state across every picker instance
  useEffect(() => {
    if (ncsColors.loading === "not-started") {
      getNcsColorsRequest();
    }
  }, [ncsColors.loading]);

  const allColors = ncsColors.loading === "has-loaded" ? ncsColors.data : [];

  const selectedColor = useMemo(() => allColors.find(color => color.code === formFieldState.value), [allColors, formFieldState.value]);

  // re-seed the gradient picker with the field's current color (or white) every time the dialog
  // is (re)opened, so it doesn't keep whatever was dialed in during a previous, cancelled visit;
  // also clear any leftover search text so a fresh open always starts on the picker/recent-colors
  // view rather than a stale filtered results list from the last time it was open
  useEffect(() => {
    if (isOpen) {
      setPickedHex(selectedColor?.hex ?? CONSTANTS.DEFAULT_PICKER_HEX);
      setSearchTerm("");
    }
  }, [isOpen]);

  const isSearching = searchTerm.trim().length > 0;

  const searchResults = useMemo(() => (isSearching ? allColors.filter(color => ncsCodeMatchesSearch(color.code, searchTerm)) : []), [allColors, isSearching, searchTerm]);

  // no manual reset-on-searchTerm-change is needed: each row's ref callback runs with null on
  // unmount and the element on mount, so the array self-syncs on its own (a previous version cleared
  // this array in a useEffect keyed on searchTerm, but that ran after the same render's ref callbacks
  // had already populated it, wiping out valid refs on every keystroke and breaking ArrowDown)
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const resultRowRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const recentColors = useMemo(() => recentCodes.map(code => allColors.find(color => color.code === code)).filter((color): color is NcsColorGet => !!color), [allColors, recentCodes]);

  // precomputed so findNearestNcsColor doesn't re-derive all ~1,695 Lab values on every drag-move tick
  const allColorsLab = useMemo(() => allColors.map(color => rgbToLab(hexToRgb(color.hex))), [allColors]);

  const nearestMatch = useMemo(() => findNearestNcsColor(allColors, hexToRgb(pickedHex), allColorsLab), [allColors, allColorsLab, pickedHex]);

  const handleSelect = (color: NcsColorGet) => {
    const rgb = hexToRgb(color.hex);
    formFieldController.onStateChange({ value: color.code, touched: true });
    onChange && onChange({ code: color.code, hex: color.hex, rgb });
    setRecentCodes(addRecentNcsColorCode(color.code));
    setIsOpen(false);
  };

  const handleConfirm = () => {
    if (nearestMatch) handleSelect(nearestMatch);
  };

  const handleClear = (event: MouseEvent) => {
    // the button lives inside the trigger TextField's adornment, which itself has an onClick that
    // opens the dialog - without stopping propagation, clearing would immediately reopen it
    event.stopPropagation();
    formFieldController.onStateChange({ value: null, touched: true });
    onChange && onChange(null);
  };

  // the trigger is a readOnly <input>, so it only opens the dialog via onClick by default -
  // keyboard users tabbing to it and pressing Enter/Space would otherwise have no way in
  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(true);
    }
  };

  const handleSearchInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && searchResults.length > 0) {
      event.preventDefault();
      resultRowRefs.current[0]?.focus();
    }
  };

  // Enter/Space are left to ButtonBase's native button behaviour, which already fires onClick on both keys
  const handleResultRowKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (index < searchResults.length - 1) resultRowRefs.current[index + 1]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (index === 0) searchInputRef.current?.focus();
      else resultRowRefs.current[index - 1]?.focus();
    }
  };

  return (
    <>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        label={label}
        placeholder={ncsColorPickerT.noColorSelected}
        value={selectedColor?.code ?? ""}
        disabled={disabled}
        error={hasValue(formFieldState.error) || hasValue(formFieldState.componentError)}
        helperText={formFieldState.error ?? formFieldState.componentError}
        onClick={() => setIsOpen(true)}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  sx={{
                    width: theme.spacing(2.5),
                    height: theme.spacing(2.5),
                    borderRadius: theme.shape.borderRadius,
                    borderStyle: "solid",
                    borderWidth: 1,
                    borderColor: theme.palette.customColors.border,
                    bgcolor: selectedColor?.hex ?? theme.palette.customColors.lightGrey
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: selectedColor && !disabled && (
              <InputAdornment position="end">
                <Tooltip title={genericTranslationsT.clear}>
                  <IconButton k={genericTranslationsK.clear} size="small" aria-label={genericTranslationsT.clear} onClick={handleClear}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            onClick: () => setIsOpen(true),
            onKeyDown: handleTriggerKeyDown
          }
        }}
        sx={{ cursor: "pointer" }}
      />

      {isOpen && (
        <GenericDialog
          headerTitle={ncsColorPickerT.dialogTitle}
          onClose={() => setIsOpen(false)}
          renderDialogActions={
            !isSearching ? <Button t={genericTranslationsT} k={genericTranslationsK} field="apply" variant="contained" onClick={handleConfirm} disabled={!nearestMatch} /> : undefined
          }
        >
          <Box sx={{ width: "100%" }}>
            {ncsColors.loading !== "has-loaded" ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: theme.spacing(30) }}>
                <CircularProgress />
              </Stack>
            ) : (
              <Stack gap={2}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={ncsColorPickerT.searchPlaceholder}
                  value={searchTerm}
                  onChange={event => setSearchTerm(event.target.value)}
                  onKeyDown={handleSearchInputKeyDown}
                  inputRef={searchInputRef}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }
                  }}
                />

                {recentColors.length > 0 && (
                  <Stack gap={1}>
                    <Typography variant="caption" color={theme.palette.customColors.darkGrey}>
                      {ncsColorPickerT.recentColors}
                    </Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {recentColors.map(color => (
                        <Tooltip key={color.code} title={color.code}>
                          <ButtonBase
                            onClick={() => handleSelect(color)}
                            aria-label={color.code}
                            sx={{
                              width: theme.spacing(4),
                              height: theme.spacing(4),
                              bgcolor: color.hex,
                              borderRadius: theme.shape.borderRadius,
                              borderStyle: "solid",
                              borderWidth: 1,
                              borderColor: theme.palette.customColors.border
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </Stack>
                )}

                {isSearching ? (
                  searchResults.length === 0 ? (
                    <Typography variant="body2" color={theme.palette.customColors.darkGrey}>
                      {ncsColorPickerT.noResultsFound}
                    </Typography>
                  ) : (
                    <Stack gap={0.5} sx={{ maxHeight: theme.spacing(30), overflowY: "auto" }}>
                      {searchResults.map((color, index) => (
                        <ButtonBase
                          key={color.code}
                          ref={el => (resultRowRefs.current[index] = el)}
                          onClick={() => handleSelect(color)}
                          onKeyDown={event => handleResultRowKeyDown(event, index)}
                          sx={{
                            justifyContent: "flex-start",
                            gap: 1,
                            padding: theme.spacing(1),
                            borderRadius: theme.shape.borderRadius,
                            "&:hover": { bgcolor: theme.palette.customColors.grey },
                            // ButtonBase suppresses the browser's default focus outline; without its own
                            // focus-visible style, keyboard-driven focus (arrow-key navigation) would be
                            // functionally correct but invisible to sighted keyboard users
                            "&.Mui-focusVisible": { bgcolor: theme.palette.customColors.grey, outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 }
                          }}
                        >
                          <Box
                            sx={{
                              width: theme.spacing(3),
                              height: theme.spacing(3),
                              borderRadius: theme.shape.borderRadius,
                              borderStyle: "solid",
                              borderWidth: 1,
                              borderColor: theme.palette.customColors.border,
                              bgcolor: color.hex
                            }}
                          />
                          <Typography variant="body2">{color.code}</Typography>
                        </ButtonBase>
                      ))}
                    </Stack>
                  )
                ) : (
                  <Stack direction="row" gap={3} alignItems="center" flexWrap="wrap">
                    <HexColorPicker color={pickedHex} onChange={setPickedHex} />

                    {/* aria-live announces the nearest-match code as it updates while dragging or
                        arrow-nudging the picker below, since neither of those actions moves focus
                        anywhere a screen reader user would otherwise notice the change */}
                    <Stack gap={1} alignItems="center" aria-live="polite">
                      {/* the raw drag position from HexColorPicker isn't a real NCS color, so only the
                          matched swatch is shown here */}
                      <Tooltip title={nearestMatch?.code ?? ""}>
                        <Box
                          sx={{
                            width: theme.spacing(5),
                            height: theme.spacing(5),
                            borderRadius: theme.shape.borderRadius,
                            borderStyle: "solid",
                            borderWidth: 2,
                            borderColor: theme.palette.primary.main,
                            bgcolor: nearestMatch?.hex ?? theme.palette.customColors.lightGrey
                          }}
                        />
                      </Tooltip>
                      {nearestMatch && (
                        <Typography variant="body2" fontWeight={theme.typography.fontWeightBold}>
                          {genericTranslationsT.selected(nearestMatch.code)}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
        </GenericDialog>
      )}
    </>
  );
};

export const NcsColorPicker = connect(mapStateToProps, ownActions)(NcsColorPickerBase);
